import { DEFAULT_TPO_SETTINGS, type TpoSettings } from '../utils/tpoSettings'
import type { TouchInput } from '../utils/patternDetection'

export type { TouchInput }

/* ------------------------------------------------------------------ */
/*  Chart settings state + chart-type family helpers                    */
/*                                                                      */
/*  Moved verbatim out of SettingsModal.tsx: these are plain data and   */
/*  pure functions consumed far beyond the modal (ChartManager,         */
/*  useChartStyles, Toolbar, the host app), and react-refresh requires  */
/*  .tsx files to export only components for HMR to stay reliable.      */
/*  SettingsModal re-exports the TYPES for existing type-only imports;  */
/*  value imports resolve here.                                         */
/* ------------------------------------------------------------------ */

export interface ChartSettings {
  // Candle colors
  candleUpColor: string
  candleDownColor: string
  candleBorderUpColor: string
  candleBorderDownColor: string
  wickUpColor: string
  wickDownColor: string
  /** Color of the Line / Area chart series (klinecharts `candle.area.lineColor`).
   *  The area fill gradient is tinted from this too. Ignored by candle/OHLC types. */
  lineColor: string
  /** Candle border visibility (the Border row's eye). False renders borders
   *  transparent. Body visibility is chart-type state (hollow candles). */
  showCandleBorder: boolean
  /** Candle wick visibility (the Wicks row's eye). */
  showCandleWicks: boolean
  /**
   * Color settings the user has EXPLICITLY picked here (a subset of
   * `CUSTOMIZABLE_COLOR_KEYS`). Everything not listed follows its default —
   * the active app theme, or the effective candle body for border/wick — so the
   * stored value is only a fallback for when that default is unavailable.
   *
   * Tracking intent explicitly is what makes theme-following predictable — the
   * previous approach inferred it by diffing against `DEFAULT_SETTINGS`, which
   * both locked the theme out of any panel carrying a stray stored color and
   * discarded a deliberate pick that happened to equal the default. See
   * `utils/themedColors.ts`.
   */
  customizedColors: string[]
  // Session
  extendedHours: boolean
  extendedHoursHighlight: boolean
  preMarketColor: string
  afterHoursColor: string
  showPreviousClose: boolean
  previousCloseColor: string
  showPrevDayHigh: boolean
  prevDayHighColor: string
  showPrevDayLow: boolean
  prevDayLowColor: string
  /** Current-day open reference line (each session day's own open). */
  showOpen: boolean
  openColor: string
  /**
   * Split-adjusted vs unadjusted prices. Default true (matches backend). When
   * flipped, ChartManager.setAdjusted reloads from the backend with
   * `&adjusted=false`. Independent of `extendedHours` — both modes can be on
   * or off independently.
   */
  adjusted: boolean
  // Canvas
  backgroundColor: string
  /** Axis tick-text color override. Empty string ⇒ follow the theme. */
  textColor: string
  /**
   * Which grid lines draw. Optional so legacy payloads (which only carry
   * `showGrid`) migrate on read via `effectiveGridMode` — a default here would
   * shadow a legacy `showGrid: false` after the shallow settings merge. The UI
   * writes BOTH keys to keep old readers working.
   */
  gridMode?: 'both' | 'horizontal' | 'vertical' | 'none'
  /** Grid line dash style. Default 'dashed' — the themes' grid look. */
  gridLineStyle: 'solid' | 'dashed' | 'dotted'
  /** Empty bars kept clear of the right edge (klinecharts offset distance). */
  rightMarginBars: number
  /** Price-axis padding above/below the plotted range, percent of pane height. */
  topMarginPct: number
  bottomMarginPct: number
  gridHorizontalColor: string
  gridVerticalColor: string
  separatorColor: string
  sessionBreakColor: string
  showGrid: boolean
  showSessionBreaks: boolean
  /**
   * Show the earnings "E" badges at the bottom of the price pane (beat/miss
   * colored, with a hover tooltip). Rendered as the EARNINGS_MARKERS system
   * indicator when the host supplies an `earnings` array. Default ON.
   */
  showEarnings: boolean
  /**
   * Show dilution levels: a dashed line at each in-play warrant's exercise price for the
   * charted ticker. The host fetches /v2/dilution warrants + passes the levels into EdgeChart,
   * which draws them via the `useDilutionLines` hook; this flag is the on/off gate. Default ON
   * for discoverability —
   * only draws for tickers that actually have in-play warrants, and only on the single-chart
   * surfaces that opt in via ChartGridConfig.dilutionAvailable (dashboard + research).
   */
  showDilutionLevels: boolean
  /**
   * Show S-3 offering price levels: a dashed line at each live (unexpired) S-3 shelf
   * offering price for the charted ticker. Sibling of `showDilutionLevels`: same
   * /v2/charts/{ticker}/metadata fetch, same `useDilutionLines` hook, gated
   * independently so a trader can toggle warrant strikes and offering prices apart.
   * Default ON for discoverability, same rationale as `showDilutionLevels`.
   */
  showOfferingLevels: boolean
  /**
   * Show price alert lines: a dashed line and a bell label on the price axis at each of the
   * user's active price alerts for the charted ticker (utils/priceAlertLine.ts), on live
   * charts. The host passes the alerts (chart-panel's useChartPriceAlerts); this flag is the
   * on/off gate. Default ON: an alert made from the chart has to show on it.
   */
  showPriceAlertLines: boolean
  /**
   * Show split markers: the "S" badge + compact ratio label (e.g. "R/S 10:1") at the
   * bottom of the price pane on each split execution day. Rendered as the SPLIT_MARKERS
   * system indicator when the host supplies a `splits` array. Default ON - splits are
   * sparse, so a no-split ticker pays nothing.
   */
  showSplits: boolean
  // Fixed price grid
  fixedPriceGrid: boolean
  fixedPriceIncrement: number
  // Crosshair
  /** Hide the crosshair entirely (the Crosshair row's eye). Default shown. */
  showCrosshair: boolean
  /** Which crosshair lines draw (horizontal = the price line). */
  crosshairMode: 'both' | 'horizontal' | 'vertical'
  crosshairColor: string
  crosshairStyle: 'dashed' | 'solid'
  crosshairLabelBg: string
  // Axes
  /**
   * Hide the permanent price & time scales. The crosshair still shows the
   * price/time value as a floating label at the chart edges (klinecharts
   * collapses the axis strip to just that label when the ticks are hidden).
   * Per-chart only — not a global key.
   */
  hideAxisScale: boolean
  /**
   * What the price (y) axis auto-fit includes. `'candles'` (default) fits the
   * candle high/low only, so a far-off overlay (a distant EMA, a pivot level)
   * cannot squash the price into a sliver of the pane — this is the
   * "Scale price chart only" behaviour traders expect from auto-scale.
   * `'indicators'` fits the candles PLUS every on-pane indicator figure, which
   * is klinecharts' native behaviour and is now opt-in. Applies to the candle
   * pane only; sub-panes always fit their own indicators. Per-chart only.
   */
  priceAxisScope: 'candles' | 'indicators'
  /**
   * Price (y) axis scale type. 'normal' plots absolute price, 'percentage'
   * plots percent change from the first visible bar, 'log' plots a logarithmic
   * price scale. Maps to klinecharts `yAxis.type`. Per-chart only.
   */
  priceScaleType: 'normal' | 'percentage' | 'log'
  /**
   * Invert the price (y) axis so higher prices render at the bottom. Maps to
   * klinecharts `yAxis.reverse`. Per-chart only.
   */
  priceScaleInvert: boolean
  /**
   * Which side the price (y) axis renders on. Maps to klinecharts
   * `yAxis.position`. Per-chart only.
   */
  priceScalePosition: 'left' | 'right'
  /**
   * Hide the timeframe + OHLC values from the top overlay tooltip row,
   * leaving only the ticker visible. Per-chart only.
   */
  hideOverlayOHLC: boolean
  /**
   * Hide the indicator label legends anchored on the left of the candle pane
   * and each sub-pane (the floating "EMA 20", "RSI 14", etc. rows). Per-chart
   * only.
   */
  hideIndicatorLabels: boolean
  /**
   * Hide the high/low price markers — the small text labels pinned to the
   * highest and lowest candles in view. Per-chart only.
   */
  hideHighLow: boolean
  /**
   * Hide the last-price label (the colored value tag at the right end of the
   * last-price line). The price line itself stays; only the label is hidden.
   * Per-chart only — kept independent of `hideAxisScale` so hidden mode still
   * shows a persistent price anchor unless this is explicitly toggled on.
   */
  hideLastPriceLabel: boolean
  /**
   * Hide the last-price LINE (the dotted line across the chart at the most
   * recent price). Independent of `hideLastPriceLabel`: either can go alone.
   */
  hideLastPriceLine: boolean
  /**
   * Hide the Pre/Post price mark (live daily charts outside regular hours: a
   * session tag + price pill + dotted line at the extended-hours price).
   */
  hideExtendedSessionPrice: boolean
  /**
   * Hide the current-bar countdown — the small "time left until this bar closes"
   * box shown just below the last-price tag on the price axis. Per-chart only;
   * shown by default (hide = false).
   */
  hideBarCountdown: boolean
  /**
   * Hide the top-center chart watermark (the faint "TICKER - Timeframe"
   * label, e.g. "AAPL - 1m"). Per-chart only.
   */
  hideWatermark: boolean
  // Timezone
  timezone: string
  // Pattern detection (support/resistance). Off by default — opt-in overlay.
  patternDetectionEnabled: boolean
  /** Confidence floor 0–1 — hides levels below this (the sensitivity dial). */
  patternDetectionMinConfidence: number
  /**
   * Beta auto-analysis toggles. Each is an opt-in, off-by-default gate for a
   * feature that is still being tuned; the Settings modal groups them under
   * "Beta features" and every surface that exposes one shows a Beta tag. Flat
   * booleans on purpose: the host merges `{...DEFAULT_SETTINGS, ...saved}`, so a
   * stored payload that predates a key picks up its default on read. A nested
   * object would NOT (see the tpoSettings normaliser).
   */
  /** Auto Fibonacci retracement anchored to the most recent major swing. */
  autoFibEnabled: boolean
  /** Minimum swing size, in ATR multiples, for the auto Fib to anchor to. */
  autoFibSwingMult: number
  /** Auto trendlines fitted through ZigZag swing highs / lows. */
  autoTrendlinesEnabled: boolean
  /** Curation budget: keep at most this many trendlines per direction. */
  autoTrendlinesMaxPerSide: number
  /** What every auto-analysis detector measures touches on: candle wicks
   *  (high / low) or candle bodies (top / bottom of the body). One shared dial,
   *  the way TrendSpider's Wick / Body input feeds all its automation. */
  autoAnalysisTouchInput: TouchInput
  /**
   * TPO / Market Profile style. Nested rather than ~13 flat keys: it applies to
   * exactly one chart style, travels to the renderer as a single object, and the
   * persistence layer stores settings as `z.record(z.unknown())` so nesting needs
   * no schema change.
   */
  tpo: TpoSettings
}

export const DEFAULT_SETTINGS: ChartSettings = {
  tpo: DEFAULT_TPO_SETTINGS,
  candleUpColor: '#22c55e',
  showCandleBorder: true,
  showCandleWicks: true,
  candleDownColor: '#ef4444',
  candleBorderUpColor: '#22c55e',
  candleBorderDownColor: '#ef4444',
  wickUpColor: '#22c55e',
  wickDownColor: '#ef4444',
  // klinecharts' default area/line blue — keeps line/area charts unchanged
  // until the user picks a color.
  lineColor: '#1677ff',
  // Empty = every themed color follows the app theme. The color values above
  // are the fallbacks used only when the theme has no value for that key.
  customizedColors: [],
  extendedHours: true,
  extendedHoursHighlight: true,
  preMarketColor: 'rgba(82,82,82,0.09)',
  afterHoursColor: 'rgba(82,82,82,0.05)',
  showPreviousClose: false,
  previousCloseColor: '#2962ff',
  showPrevDayHigh: false,
  prevDayHighColor: '#26a69a',
  showPrevDayLow: false,
  prevDayLowColor: '#f23645',
  showOpen: false,
  openColor: '#eab308',
  adjusted: true,
  // Canvas background is theme-driven via the --chart-canvas CSS variable.
  // Inline `hsl(var(...))` resolves at paint time and tracks light/dark theme
  // switches automatically. No user-configurable override.
  backgroundColor: 'hsl(var(--chart-canvas))',
  gridHorizontalColor: '#1e222d',
  gridVerticalColor: '#1e222d',
  separatorColor: '#2a2e39',
  // Visibly lighter than the grid — the old #2a2e39 default vanished
  // against the dark canvas.
  sessionBreakColor: '#758696',
  showGrid: true,
  // Blank means "follow the theme": `useChartStyles` then resolves it through
  // `defaultAxisTextColor`, which picks #8f8fa8 on a dark canvas and #525252 on
  // a light one. This was hardcoded to '#a3a3a3' (2026-08-10), which made the
  // canvas-aware default added nine days later unreachable for any chart on
  // defaults — the Settings swatch showed the themed colour while the canvas
  // painted the light-theme gray. Charts with '#a3a3a3' explicitly persisted
  // keep it; only new charts pick this up (Chris, 2026-09-09).
  textColor: '',
  gridLineStyle: 'dashed',
  rightMarginBars: 5,
  topMarginPct: 10,
  bottomMarginPct: 10,
  showSessionBreaks: false,
  showEarnings: true,
  showDilutionLevels: true,
  showOfferingLevels: true,
  showPriceAlertLines: true,
  showSplits: true,
  fixedPriceGrid: false,
  fixedPriceIncrement: 0.5,
  crosshairColor: '#525252',
  crosshairStyle: 'dashed',
  showCrosshair: true,
  crosshairMode: 'both',
  crosshairLabelBg: '#363a45',
  hideAxisScale: false,
  priceAxisScope: 'candles',
  priceScaleType: 'normal',
  priceScaleInvert: false,
  priceScalePosition: 'right',
  hideOverlayOHLC: false,
  hideIndicatorLabels: false,
  hideHighLow: false,
  hideLastPriceLabel: false,
  hideLastPriceLine: false,
  hideExtendedSessionPrice: false,
  hideBarCountdown: false,
  hideWatermark: false,
  timezone: 'America/New_York',
  patternDetectionEnabled: false,
  patternDetectionMinConfidence: 0.4,
  autoFibEnabled: false,
  autoFibSwingMult: 3,
  autoTrendlinesEnabled: false,
  autoTrendlinesMaxPerSide: 3,
  autoAnalysisTouchInput: 'wicks',
}

/**
 * A saved snapshot of chart settings. The chart receives a list of
 * profiles via props; the application owns persistence and supplies the
 * `id` after creation.
 */
export interface ChartSettingsProfile {
  id: string
  name: string
}

/** Style family: which group of color settings a chart type belongs to.
 *  Candles covers solid + hollow; Line covers line + area; Bars is OHLC;
 *  TPO is the Market Profile chart style; Heikin is the averaged-candle style
 *  (Beta), solid + hollow. Hollow and area-fill are MODIFIERS within their
 *  family rather than families of their own — see hollowToggled /
 *  areaFillToggled / heikinHollowToggled below. Heikin is its OWN family, not
 *  a candles modifier: the pickers highlight by family, and the candles
 *  modifier helpers write `candle_*` strings, which would silently drop a chart
 *  out of Heikin Ashi. */
export type StyleGroup = 'candles' | 'line' | 'bars' | 'tpo' | 'heikin'

/** The two Heikin Ashi chart-type strings (solid, hollow). */
export const HEIKIN_ASHI_TYPE = 'heikin_ashi'
export const HEIKIN_ASHI_HOLLOW_TYPE = 'heikin_ashi_hollow'

export function styleGroupOf(chartType: string | undefined): StyleGroup {
  if (chartType === 'line' || chartType === 'area') return 'line'
  if (chartType === 'ohlc') return 'bars'
  if (chartType === 'tpo') return 'tpo'
  if (chartType === HEIKIN_ASHI_TYPE || chartType === HEIKIN_ASHI_HOLLOW_TYPE) return 'heikin'
  return 'candles'
}

/**
 * Whether the chart canvas reads as dark, so the axis-text default can pick a
 * gray tuned for it. `backgroundColor` is a concrete hex when the user set one,
 * else the default `hsl(var(--chart-canvas))` token, which we resolve live from
 * the DOM and read by its HSL lightness. Anything unresolvable (e.g. jsdom in a
 * unit test) reads as not-dark, so the fallback is today's light-theme gray.
 * A missing `backgroundColor` is the default canvas: settings without one
 * reached here in prod and crashed the chart on `.trim()`.
 */
function isDarkCanvas(backgroundColor: string | undefined): boolean {
  const bg = (backgroundColor ?? DEFAULT_SETTINGS.backgroundColor).trim()
  if (bg.startsWith('#')) {
    const v = parseInt(bg.slice(1), 16)
    const r = ((v >> 16) & 0xff) / 255
    const g = ((v >> 8) & 0xff) / 255
    const b = (v & 0xff) / 255
    // Rec. 601 luminance, 0 (black) to 1 (white).
    return 0.299 * r + 0.587 * g + 0.114 * b < 0.5
  }
  if (typeof document === 'undefined') return false
  const token = getComputedStyle(document.documentElement).getPropertyValue('--chart-canvas').trim()
  // Token looks like "225 26% 9%" (hue sat% light%); lightness is the last %.
  const pcts = token.match(/(\d+(?:\.\d+)?)%/g)
  if (!pcts || pcts.length === 0) return false
  const lightness = parseFloat(pcts[pcts.length - 1])
  return Number.isFinite(lightness) && lightness < 50
}

/**
 * Painted default for the axis / chart text when the user has not picked a
 * `textColor`. Dark canvases (Edge Dark, Edge Black, Glacier, any dark preset,
 * or a user-set dark hex background on a light theme) get a slightly cooler
 * gray. Light canvases (Edge Light, Sahara) get #525252, the same swatch gray
 * as the light crosshair: the original #a3a3a3 was ~2.5:1 on white and read as
 * faded (Chris, 2026-09-25).
 */
export function defaultAxisTextColor(backgroundColor: string | undefined): string {
  return isDarkCanvas(backgroundColor) ? '#8f8fa8' : '#525252'
}

/**
 * The chart type a family switches to, preserving the family's modifier where
 * one exists. Kept as a pure mapping so no chart-type string is invented at a
 * call site.
 */
export function chartTypeForGroup(group: StyleGroup): string {
  if (group === 'line') return 'line'
  if (group === 'bars') return 'ohlc'
  if (group === 'tpo') return 'tpo'
  if (group === 'heikin') return HEIKIN_ASHI_TYPE
  return 'candle_solid'
}

/** Hollow is a modifier of the Heikin family too: solid ⇄ hollow. */
export function isHollowHeikinAshi(chartType: string | undefined): boolean {
  return chartType === HEIKIN_ASHI_HOLLOW_TYPE
}

export function heikinHollowToggled(hollow: boolean): string {
  return hollow ? HEIKIN_ASHI_HOLLOW_TYPE : HEIKIN_ASHI_TYPE
}

/**
 * Read-side migration: legacy payloads carry only `showGrid`.
 *
 * `showGrid === false` wins over any stored gridMode — the eye writes
 * showGrid while PRESERVING gridMode (so re-showing restores the line
 * choice), which means a set gridMode must never resurrect a hidden grid.
 */
export function effectiveGridMode(
  settings: Pick<ChartSettings, 'showGrid' | 'gridMode'>,
): NonNullable<ChartSettings['gridMode']> {
  if (settings.showGrid === false) return 'none'
  return settings.gridMode ?? 'both'
}

/** Hollow is a modifier of the candles family: solid ⇄ stroke. */
export function isHollowCandles(chartType: string | undefined): boolean {
  return chartType === 'candle_stroke'
}
export function hollowToggled(hollow: boolean): string {
  return hollow ? 'candle_stroke' : 'candle_solid'
}

/** Area fill is a modifier of the line family. `area` and `line` were never
 *  distinct chart types — both are klinecharts' `area` series, differing only in
 *  whether the gradient fill is transparent (see chartTypeStyles.ts). */
export function isAreaFilled(chartType: string | undefined): boolean {
  return chartType === 'area'
}
export function areaFillToggled(filled: boolean): string {
  return filled ? 'area' : 'line'
}
